/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';
import {
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  useId,
  useMemo,
} from 'react';

import {
  FormFieldLabel,
  FormFieldMessage,
  useFormField,
} from '../_common/form-control';
import { customLabelAndNoAriaLabelWarningMessage } from '../_common/messages';
import { type CommonProps, type TooltipObjectProps } from '../_common/types';
import { useSemiControlledInput } from '../_common/use-semi-controlled-input';
import { needleWarningMessage } from '../_common/utils';
import { XMarkIconOutline } from '../icons/generated/heroIcons/XMarkIconOutline';
import { LoadingSpinner } from '../loading-spinner';
import { Skeleton } from '../skeleton';

/**
 * @remarks
 * When `moreInformationText` is provided, a tooltip is rendered next to the label, and the `tooltipProps` prop is used to configure the tooltip. The `TooltipObjectProps` type is defined as: `{ root?: Partial<React.ComponentProps<typeof Tooltip>>; trigger?: Partial<React.ComponentProps<typeof Tooltip.Trigger>>; content?: Partial<React.ComponentProps<typeof Tooltip.Content>>; }`, see the Tooltip component for more details.
 *
 * @remarks
 * When `isSkeletonLoading` is true, the input is rendered as a loading skeleton and the `skeletonProps` prop is used to configure the skeleton. For more information, see the Skeleton documentation.
 */
type TextInputProps = {
  /** Assistive text displayed below the input */
  helpText?: string | ReactNode;

  /** Error message displayed below the input. When provided, it overrides `helpText` and shows an error icon */
  errorText?: string | ReactNode;

  /** Leading element rendered inside the input */
  leadingElement?: ReactElement;

  /** Trailing element rendered inside the input */
  trailingElement?: ReactElement;

  /** Size of the input */
  size?: 'small' | 'medium' | 'large';

  /** Whether the input is read-only */
  isReadOnly?: boolean;

  /**
   *  Whether to display the Required/Optional label next to the field label.
   *  Shows Required if isRequired is true, otherwise shows Optional.
   * @default false
   */
  // TODO v5: inconsistent naming. The same switch is `hasRequiredIndicator` on
  // Combobox and on the internal form control this maps onto, and `isOptional`
  // on TextArea. The name also needs the disable below, which a name using one
  // of the allowed boolean prefixes would not.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  showRequiredOrOptionalLabel?: boolean;

  /** Whether the input is disabled */
  isDisabled?: boolean;

  /** Whether the input is required */
  isRequired?: boolean;

  /** The current value of the input */
  value?: React.ComponentPropsWithoutRef<'input'>['value'];

  /** Placeholder text displayed when the input is empty */
  placeholder?: React.ComponentPropsWithoutRef<'input'>['placeholder'];

  /** Callback function triggered when the input value changes */
  onChange?: React.ComponentPropsWithoutRef<'input'>['onChange'];

  /** Whether the input should take the full available width */
  isFluid?: boolean;

  /** Text displayed in the information tooltip shown next to the label */
  moreInformationText?: React.ReactNode;

  // TODO v5: inconsistent naming. Every form control names itself differently:
  // the label is optional here and a design with no room for it reaches for
  // htmlAttributes['aria-label'], TextArea, Select, Checkbox, Radio and Switch
  // expose an `ariaLabel` prop instead, and Combobox requires the label and
  // clips it with `hasVisuallyHiddenLabel`.
  /** The label displayed above the input */
  label?: string | React.ReactNode;

  /** Props for the Tooltip component used by the information icon */
  tooltipProps?: TooltipObjectProps;

  /** Whether to render skeletons instead of content */
  isSkeletonLoading?: boolean;

  /** Additional props forwarded to the underlying Skeleton components */
  skeletonProps?: React.ComponentProps<typeof Skeleton>;

  /** Whether to show the clear button */
  isClearable?: boolean;

  /** Whether to show the loading spinner */
  isLoading?: boolean;
};

/**
 *
 *
 * TextInput Component
 *
 *
 */

export const TextInput = ({
  label,
  isFluid,
  errorText,
  helpText,
  leadingElement,
  trailingElement,
  showRequiredOrOptionalLabel = false,
  moreInformationText,
  size = 'medium',
  placeholder,
  value,
  tooltipProps,
  htmlAttributes,
  isDisabled,
  isReadOnly,
  isRequired,
  onChange,
  isClearable = false,
  className,
  style,
  isSkeletonLoading = false,
  isLoading = false,
  skeletonProps,
  ref,
  ...restProps
}: CommonProps<'input', TextInputProps>) => {
  const [currentValue, handleChange] = useSemiControlledInput({
    inputType: 'text',
    isControlled: value !== undefined,
    onChange,
    state: value ?? '',
  });
  const hintId = useId();
  const canClear = isClearable && isDisabled !== true;
  const shouldRenderHint = canClear || isLoading;
  const { field, controlProps } = useFormField({
    errorText,
    // The clear/loading hint describes the input alongside the message.
    extraDescribedByIds: [shouldRenderHint && hintId],
    hasRequiredIndicator: showRequiredOrOptionalLabel,
    helpText,
    isRequired,
    label,
  });

  const containerWrappingClasses = classNames(`ndl-text-input`, className, {
    'ndl-disabled': isDisabled,
    'ndl-has-error': errorText,
    'ndl-has-icon': leadingElement || trailingElement || errorText,
    'ndl-has-leading-icon': leadingElement,
    'ndl-has-trailing-icon': trailingElement || errorText,
    'ndl-large': size === 'large',
    'ndl-medium': size === 'medium',
    'ndl-read-only': isReadOnly,
    'ndl-small': size === 'small',
  });

  const hasEmptyLabelValue =
    label === undefined || label === null || label === '';

  const labelWrappingClasses = classNames('ndl-text-input-label', {
    'ndl-fluid': isFluid,
  });

  const combinedHtmlInputAttributes: InputHTMLAttributes<HTMLInputElement> = {
    ...htmlAttributes,
    className: classNames('ndl-input', htmlAttributes?.className),
  };
  const ariaLabel = combinedHtmlInputAttributes['aria-label'];
  const isCustomLabel = Boolean(label) && typeof label !== 'string';
  const hasCustomLabelAndNoAriaLabel =
    isCustomLabel && (ariaLabel === undefined || ariaLabel === '');

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (canClear && event.key === 'Escape' && Boolean(currentValue)) {
      event.preventDefault();
      event.stopPropagation();
      handleChange?.({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    htmlAttributes?.onKeyDown?.(event);
  };

  useMemo(() => {
    if (!label && !ariaLabel) {
      needleWarningMessage(
        'A TextInput without a label does not have an aria label, be sure to include an aria label for screen readers. Link: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI',
      );
    }

    if (hasCustomLabelAndNoAriaLabel) {
      needleWarningMessage(customLabelAndNoAriaLabelWarningMessage);
    }
  }, [label, ariaLabel, hasCustomLabelAndNoAriaLabel]);

  return (
    <div className={containerWrappingClasses} style={style}>
      {/* We enable implicit label wrapping */}
      {/* Source: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI */}
      <label className={labelWrappingClasses}>
        {!hasEmptyLabelValue && (
          <FormFieldLabel
            field={field}
            size={size}
            association="implicit"
            moreInformationText={moreInformationText}
            tooltipProps={tooltipProps}
            wrap={(row) => (
              <Skeleton
                onBackground="weak"
                shape="rectangular"
                {...skeletonProps}
                isLoading={isSkeletonLoading}
              >
                {row}
              </Skeleton>
            )}
          />
        )}
        <Skeleton
          onBackground="weak"
          shape="rectangular"
          {...skeletonProps}
          isLoading={isSkeletonLoading}
        >
          <div className="ndl-input-wrapper">
            {(leadingElement || (isLoading && !trailingElement)) && (
              <div className="ndl-element-leading ndl-element">
                {isLoading ? (
                  <LoadingSpinner
                    size={size === 'large' ? 'medium' : 'small'}
                    className={
                      size === 'large'
                        ? 'ndl-medium-spinner'
                        : 'ndl-small-spinner'
                    }
                  />
                ) : (
                  leadingElement
                )}
              </div>
            )}
            <div
              className={classNames('ndl-input-container', {
                'ndl-clearable': isClearable,
              })}
            >
              <input
                ref={ref}
                readOnly={isReadOnly}
                disabled={isDisabled}
                required={isRequired}
                value={currentValue}
                placeholder={placeholder}
                type="text"
                onChange={handleChange}
                {...controlProps}
                {...combinedHtmlInputAttributes}
                onKeyDown={handleInputKeyDown}
                {...restProps}
              />
              {shouldRenderHint && (
                <span
                  id={hintId}
                  className="ndl-text-input-hint"
                  aria-hidden={true}
                >
                  {isLoading && 'Loading '}
                  {canClear && 'Press Escape to clear input.'}
                </span>
              )}
              {canClear && Boolean(currentValue) && (
                <div className="ndl-element-clear ndl-element">
                  <button
                    tabIndex={-1}
                    aria-hidden={true}
                    type="button"
                    title="Clear input (Esc)"
                    onClick={() => {
                      handleChange?.({
                        target: { value: '' },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                  >
                    <XMarkIconOutline />
                  </button>
                </div>
              )}
            </div>
            {trailingElement && (
              <div className="ndl-element-trailing ndl-element">
                {isLoading && !leadingElement ? (
                  <LoadingSpinner
                    size={size === 'large' ? 'medium' : 'small'}
                    className={
                      size === 'large'
                        ? 'ndl-medium-spinner'
                        : 'ndl-small-spinner'
                    }
                  />
                ) : (
                  trailingElement
                )}
              </div>
            )}
          </div>
        </Skeleton>
      </label>

      <FormFieldMessage
        field={field}
        size={size}
        wrap={(content) => (
          <Skeleton
            onBackground="weak"
            shape="rectangular"
            {...skeletonProps}
            isLoading={isSkeletonLoading}
          >
            {content}
          </Skeleton>
        )}
      />
    </div>
  );
};
